<template>
  <div>
    <div class="appointment-details">
      <status-manager
        class="mb-4"
        :read-only="readOnly"
        :appointment="appointment"
        :warehouse-triggers="warehouseTriggers"
        :should-edit-status="shouldEditStatus"></status-manager>

      <section class="sidebar-content">
        <div class="sidebar">
          <details-content-box
            :header="t('global.specialtyTerms.carrierContacts')"
            data-testid="appointment-details-carrier-box">
            <div class="d-flex align-center mb-4">
              <favorite-button
                v-if="appointment.user.company?.id"
                :org="org"
                :carrier="appointment.user"
                @toggle-favorite="toggleFavoriteCarrier"
                class="mr-2"></favorite-button>
              <company-presenter :user="{ id: appointment.userId }" />
            </div>

            <!-- NOTE: we do not provide the name here because it will be outdated in case
                  the carrier is changed (since it comes from the appointment prop) which,
                  as of now, it not updated whenever any change happens.
                  This is one caveat of:
                  1) the disparity in how different components are consuming the dialog
                  2) not having a reliable update/sync mechanism that applies to all details dialog hosts -->
            <user-presenter
              :user="{ id: appointment.userId }"
              :display-fields="['name', 'email', 'phone']" />
            <company-presenter :user="{ id: appointment.userId }" :display-fields="['scac']" />

            <div class="mt-4" v-if="$rolePermissions.canUpdateAppointment">
              <!-- TODO: there is no carrier key in the EditableDetailsFields enum -->
              <outline-button
                v-if="!fieldsInEditMode.includes(EditableDetailsFields.carrier)"
                small
                inline
                class="align-self-start"
                @click="handleEditClick(EditableDetailsFields.carrier)">
                {{ t('appointment.dialogs.appointmentDetailsDialog.buttons.changeCarrier') }}
              </outline-button>
              <template v-else>
                <carrier-select
                  outlined
                  v-model="selectedCarrier"
                  hide-icon
                  dense
                  data-testid="appointment-details-carrier-select"
                  truncate-as-whole
                  entry-point="Appointment Details Modal"></carrier-select>
                <div class="d-flex align-center">
                  <v-spacer></v-spacer>
                  <outline-button
                    small
                    class="mr-2"
                    @click="handleNevermindClick(EditableDetailsFields.carrier)">
                    {{ t('global.buttons.nevermind') }}
                  </outline-button>
                  <primary-button
                    small
                    @click="
                      saveField(EditableDetailsFields.carrier, selectedCarrier?.id, 'userId')
                    ">
                    {{ t('global.buttons.save') }}
                  </primary-button>
                </div>
              </template>
            </div>
          </details-content-box>
          <details-content-box
            :is-editable="false"
            v-if="isFeatureEnabledForWarehouse(appointment.warehouse, 'yms')">
            <yard-view-appointment-details-box :appointment-id="appointment.id" />
          </details-content-box>

          <details-content-box
            :header="t('appointment.dialogs.appointmentDetailsDialog.headers.appointmentInfo')"
            class="mb-0 full-height">
            <div class="data">
              <div class="data-group">
                <span class="data-header">{{ t('global.specialtyTerms.docks') }}</span>
                <span>{{ appointment.dock.name }}</span>
              </div>
              <div class="data-group action-group">
                <div class="d-flex flex-column full-width">
                  <span class="data-header">{{ t('global.specialtyTerms.loadTypes') }}</span>
                  <!-- NOTE: we do not provide the name here because it will be outdated in case
                  the load type is changed (since it comes from the appointment prop) which,
                  as of now, it not updated whenever any change happens -->
                  <load-type-presenter
                    v-if="!fieldsInEditMode.includes(EditableDetailsFields.loadType)"
                    :load-type="{ id: loadTypeId }" />
                  <template v-else>
                    <load-type-select
                      class="mt-1"
                      outlined
                      small
                      :clearable="false"
                      hide-icon
                      :selected-warehouse="appointment.warehouse"
                      :selected-docks="[appointment.dock]"
                      :docks="[appointment.dock]"
                      show-only-assigned-load-types
                      return-object
                      :model-value="loadTypeId"
                      @update:model-value="value => (loadTypeId = value.id)" />
                    <div class="d-flex justify-end">
                      <outline-button
                        small
                        class="mr-2"
                        @click="handleNevermindClick(EditableDetailsFields.loadType)">
                        {{ t('global.buttons.nevermind') }}
                      </outline-button>
                      <primary-button
                        :disabled="loadTypeId === appointment.loadTypeId"
                        small
                        :loading="fieldsInLoadingState.includes(EditableDetailsFields.loadType)"
                        @click="saveField(EditableDetailsFields.loadType, loadTypeId)">
                        {{ t('global.buttons.save') }}
                      </primary-button>
                    </div>
                  </template>
                </div>
                <v-btn
                  variant="text"
                  icon
                  @click="handleEditClick(EditableDetailsFields.loadType)"
                  v-if="
                    !fieldsInEditMode.includes(EditableDetailsFields.loadType) &&
                    $rolePermissions.canUpdateAppointment
                  ">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div class="data-group">
                <span class="data-header">{{ t('global.specialtyTerms.warehouses') }}</span>
                <span>{{ appointment.warehouse.name }}</span>
              </div>
              <div class="data-group action-group">
                <div class="d-flex flex-column">
                  <span class="data-header">
                    {{
                      t('appointment.dialogs.appointmentDetailsDialog.headers.confirmationNumber')
                    }}
                  </span>
                  <span>{{ appointment.confirmationNumber }}</span>
                </div>
                <copy-content
                  :content="appointment.confirmationNumber"
                  :tooltip="
                    t(
                      'appointment.dialogs.appointmentDetailsDialog.tooltips.clickToCopyConfirmationNumber'
                    )
                  "></copy-content>
              </div>
              <div class="data-group action-group">
                <div class="d-flex flex-column">
                  <span class="data-header">
                    {{ t('appointment.dialogs.appointmentDetailsDialog.headers.appointmentId') }}
                  </span>
                  <span>{{ appointment.id }}</span>
                </div>
                <copy-content
                  test-id="appointment-details-copy-appointment-id"
                  :content="appointment.id"
                  :tooltip="
                    t(
                      'appointment.dialogs.appointmentDetailsDialog.tooltips.clickToCopyAppointmentId'
                    )
                  "></copy-content>
              </div>
            </div>
          </details-content-box>
        </div>
        <div class="content flex-1">
          <details-content-box
            is-dense
            :is-editable="!fieldsInEditMode.includes(EditableDetailsFields.eta)"
            :field="EditableDetailsFields.eta"
            :header="t('appointment.dialogs.appointmentDetailsDialog.headers.carrierEta')"
            v-if="appointment.status === novaCore.AppointmentStatus.Scheduled">
            <template #edit-button="{ field }">
              <outline-button small @click="handleEditClick(field)">
                {{ t('appointment.dialogs.appointmentDetailsDialog.buttons.updateEta') }}
              </outline-button>
            </template>

            <template v-if="!fieldsInEditMode.includes(EditableDetailsFields.eta)">
              <span class="text--color-text-tertiary" v-if="!appointment.eta">
                {{
                  t(
                    'appointment.dialogs.appointmentDetailsDialog.messages.noEtaProvidedByTheCarrier'
                  )
                }}
              </span>
              <template v-else>
                <div class="d-flex align-center">
                  <span>{{ computedEta }}</span>
                  <v-chip
                    v-if="appointment.eta"
                    size="small"
                    :color="getEtaConditionMeta(etaCondition).color"
                    :class="getEtaConditionMeta(etaCondition).classes"
                    class="ml-2">
                    <span class="font-size-x-small">
                      {{ novaCore.breakWordsAtCaps(etaCondition) }}
                    </span>
                  </v-chip>
                </div>
              </template>
            </template>
            <template v-else>
              <set-eta-form
                ref="setEtaForm"
                :appointment="appointment"
                @cancel="handleNevermindClick"
                @condition-updated="updateEtaCondition"></set-eta-form>
            </template>
          </details-content-box>
          <details-content-box
            :header="t('appointment.dialogs.appointmentDetailsDialog.headers.appointmentTags')"
            is-dense
            test-id="appointment-details-dialog-tag-field">
            <tag-manager
              v-if="$rolePermissions.canUpdateAppointmentTags"
              outlined
              data-testid="appointment-details-dialog-tags"
              v-model="tags"
              :outlined-chips="true"
              small-chips
              :should-enable-custom-tags="true"
              :read-only="!$rolePermissions.canUpdateAppointmentTags || readOnly"
              :placeholder="t('appointment.dialogs.appointmentDetailsDialog.placeholders.addTags')"
              hide-instructions
              :allowTagDelete="!lockTagEditing" />
            <div v-else>
              {{ appointment.tags?.length > 0 ? appointment.tags?.join(', ') : '&#8212;' }}
            </div>
          </details-content-box>

          <details-content-box
            :header="refNumDisplayName"
            v-if="shouldShowRefNumber"
            :is-editable="
              !fieldsInEditMode.includes(EditableDetailsFields.refNumber) &&
              $rolePermissions.canUpdateAppointment
            "
            :field="EditableDetailsFields.refNumber"
            is-dense
            @edit-click="handleEditClick"
            test-id="appointment-details-dialog-ref-number-field">
            <span
              v-if="!fieldsInEditMode.includes(EditableDetailsFields.refNumber)"
              class="field-value">
              {{ appointment.refNumber || '&#8212;' }}
            </span>
            <template v-else>
              <text-field
                outlined
                v-model="refNumber"
                @keydown.enter="saveField(EditableDetailsFields.refNumber, refNumber)"></text-field>
              <div class="d-flex justify-end">
                <outline-button
                  small
                  class="mr-2"
                  @click="handleNevermindClick(EditableDetailsFields.refNumber)">
                  {{ t('global.buttons.nevermind') }}
                </outline-button>
                <primary-button
                  :disabled="
                    refNumber === appointment.refNumber ||
                    fieldsInLoadingState.includes(EditableDetailsFields.refNumber)
                  "
                  small
                  :loading="fieldsInLoadingState.includes(EditableDetailsFields.refNumber)"
                  @click="saveField(EditableDetailsFields.refNumber, refNumber)">
                  {{ t('global.buttons.save') }}
                </primary-button>
              </div>
            </template>
          </details-content-box>

          <details-content-box
            :header="t('global.basicTerms.notes', 2)"
            :is-dense="!appointment.notes"
            :is-editable="
              !fieldsInEditMode.includes(EditableDetailsFields.notes) &&
              $rolePermissions.canUpdateAppointment
            "
            :field="EditableDetailsFields.notes"
            @edit-click="handleEditClick"
            test-id="appointment-details-dialog-notes-field">
            <div
              v-if="!fieldsInEditMode.includes(EditableDetailsFields.notes)"
              class="html-content field-value"
              v-html="appointment.notes || '&#8212;'"></div>
            <template v-else>
              <rich-text-input
                class="flex-fill"
                :editor-config="{
                  plugins: ['Essentials', 'Paragraph'],
                  toolbar: { items: [] }
                }"
                v-model="appointmentDataRefs[EditableDetailsFields.notes].value"></rich-text-input>
              <div class="d-flex justify-end mt-6">
                <outline-button
                  small
                  class="mr-2"
                  @click="handleNevermindClick(EditableDetailsFields.notes)">
                  {{ t('global.buttons.nevermind') }}
                </outline-button>
                <primary-button
                  :disabled="
                    appointmentDataRefs[EditableDetailsFields.notes] === appointment.notes ||
                    fieldsInLoadingState.includes(EditableDetailsFields.notes)
                  "
                  small
                  :loading="fieldsInLoadingState.includes(EditableDetailsFields.notes)"
                  @click="
                    saveField(
                      EditableDetailsFields.notes,
                      appointmentDataRefs[EditableDetailsFields.notes].value
                    )
                  ">
                  {{ t('global.buttons.save') }}
                </primary-button>
              </div>
            </template>
          </details-content-box>

          <details-content-box
            :header="t('appointment.dialogs.appointmentDetailsDialog.headers.bookingDetails')"
            :is-dense="Object.keys(customFields).length === 0"
            :is-editable="Boolean($rolePermissions.canUpdateAppointment)"
            :field-name="EditableDetailsFields.customFields"
            @edit-click="handleEditClick(EditableDetailsFields.customFields)"
            test-id="appt-details-dialog-custom-fields">
            <span v-if="!Object.keys(customFields).length">&#8212;</span>
            <div class="data" v-else>
              <div
                class="data-group no-divider"
                v-for="customField in Object.values(customFields)"
                :key="customField.key">
                <span class="data-header">{{ customField.label }}</span>
                <span
                  :data-testid="`${toKebabCase(customField.label)}-value`"
                  v-html="customField.value"></span>
              </div>
            </div>
          </details-content-box>

          <details-content-box
            :header="
              t('appointment.dialogs.appointmentDetailsDialog.headers.loadTypeBookingDetails')
            "
            test-id="appt-details-dialog-custom-fields"
            v-if="loadTypeTriggers.length > 0">
            <custom-forms-multiple-trigger-form
              :warehouse-id="warehouse.id"
              :is-editable="$rolePermissions.canUpdateAppointment"
              @update:form-field="handleFormFieldUpdate"
              :object-id="appointment.id"
              :triggers="loadTypeTriggers"
              :timezone="warehouse.timezone"
              :components-mapping="customFormsComponentsMapping"
              :military-time-enabled="isMilitaryTimeEnabled" />
          </details-content-box>

          <details-content-box
            :header="t('appointment.dialogs.appointmentDetailsDialog.headers.emailSubscribers')"
            test-id="appointment-details-dialog-email-field"
            is-dense
            class="mb-0">
            <email-list-field
              outlined
              small-chips
              v-model="ccEmails"
              ref="emailFieldList"
              v-if="$rolePermissions.canUpdateAppointmentEmailCCs" />
            <div v-else>
              {{ appointment.ccEmails?.length > 0 ? appointment.ccEmails.join(', ') : '&#8212;' }}
            </div>
          </details-content-box>
        </div>
      </section>
    </div>

    <edit-custom-fields-dialog
      external-activator
      v-if="fieldsInEditMode.includes(EditableDetailsFields.customFields)"
      @close="novaCore.removeArrayItem(fieldsInEditMode, EditableDetailsFields.customFields)"
      :appointment="novaCore.deepClone(appointment)"
      :show-dialog="fieldsInEditMode.includes(EditableDetailsFields.customFields)"
      @save="data => saveField(EditableDetailsFields.customFields, data)" />
  </div>
</template>

<script>
import AppointmentDetailsBase from '@/modules/appointments/components/details/AppointmentDetailsBase.vue';
import DetailsContentBox from '@/modules/appointments/components/details/DetailsContentBox.vue';
import { computed, ref, watch } from 'vue';
import { isEqual, capitalize, uniq } from 'lodash';
import { EditableDetailsFields } from '@/enums';
import appointmentService from '@satellite/services/appointment-service';
import EditCustomFieldsDialog from '@/modules/appointments/components/details/EditCustomFieldsDialog.vue';
import {
  useNovaCore,
  useOrg,
  useEta,
  useNotify,
  useCustomForms,
  useMixpanel,
  useEventHub
} from '@/composables';
import MixpanelEvents from '@/enums/MixpanelEvents';
import { useStore } from 'vuex';
import CustomFormsMultipleTriggerForm from '@satellite/components/elements/CustomFormsMultipleTriggerForm.vue';
import LoadTypePresenter from '@satellite/components/elements/presenters/load_type/LoadTypePresenter.vue';
import CompanyPresenter from '@satellite/components/elements/presenters/user/CompanyPresenter.vue';
import UserPresenter from '@satellite/components/elements/presenters/user/UserPresenter.vue';
import {
  getTranslatedRefNumberDisplayName,
  isFeatureEnabledForWarehouse,
  toKebabCase
} from '@nova/core';
import notificationsHelper from '@satellite/helpers/notifications.js';
import YardViewAppointmentDetailsBox from '@/modules/yard/components/AppointmentDetailsBox/YardViewAppointmentDetailsBox.vue';
import { useI18n } from 'vue-i18n';

export default {
  components: {
    EditCustomFieldsDialog,
    DetailsContentBox,
    CustomFormsMultipleTriggerForm,
    LoadTypePresenter,
    CompanyPresenter,
    UserPresenter,
    YardViewAppointmentDetailsBox
  },
  extends: AppointmentDetailsBase,
  setup(props) {
    const { t } = useI18n();
    const novaCore = useNovaCore();
    const notify = useNotify();
    const store = useStore();
    const { orgRef: org } = useOrg();
    const loadTypeId = ref(props.appointment.loadTypeId);
    const refNumber = ref(props.appointment.refNumber);
    const notes = ref(props.appointment.notes);
    const customFields = ref(props.appointment.customFields);
    const selectedCarrier = ref(props.appointment.user);
    const mixpanel = useMixpanel();
    const eventHub = useEventHub();

    const isMilitaryTimeEnabled = computed(() =>
      store.getters['Settings/isMilitaryTimeEnabled']({
        ...warehouse.value,
        org
      })
    );
    const warehouse = computed(() => props.appointment?.dock?.warehouse);

    const { cleanUpCustomFormsData, customFormsComponentsMapping } = useCustomForms(
      warehouse.value.timezone,
      isMilitaryTimeEnabled
    );

    const appointmentDataRefs = {
      // Map refs using the EditableDetailsFields enum keys
      [EditableDetailsFields.loadType]: loadTypeId,
      [EditableDetailsFields.refNumber]: refNumber,
      [EditableDetailsFields.notes]: notes,
      [EditableDetailsFields.customFields]: customFields
    };

    const fieldsInEditMode = ref([]);
    const fieldsInLoadingState = ref([]);
    function handleEditClick(fieldName) {
      fieldsInEditMode.value.push(fieldName);
    }

    const ccEmails = ref(props.appointment.ccEmails);

    async function updateCCEmails(oldVal) {
      try {
        await axios.patch(`appointment/${props.appointment.id}`, {
          ccEmails: ccEmails.value
        });
      } catch (e) {
        ccEmails.value = oldVal;
      }
    }

    function handleNevermindClick(fieldName) {
      novaCore.removeArrayItem(fieldsInEditMode.value, fieldName);
      const refToUpdate = appointmentDataRefs[fieldName];
      if (refToUpdate) {
        refToUpdate.value = props.appointment[fieldName];
      }
    }

    async function saveField(fieldName, data, fieldKey = null) {
      if (fieldsInLoadingState.value.includes(fieldName)) return;
      fieldsInLoadingState.value.push(fieldName);
      try {
        const response = await appointmentService.updateAppointment(
          props.appointment.id,
          {
            [fieldKey ?? fieldName]: data ?? appointmentDataRefs[fieldName].value
          },
          { includeMetaData: true },
          { suppressNotification: true }
        );
        notificationsHelper.showAppointmentSuccessNotification({
          appointmentData: response.data?.data,
          notifyFn: notify,
          defaultMessage: t(
            'appointment.dialogs.appointmentDetailsDialog.messages.appointmentHasBeenUpdated'
          ),
          isUpdate: true
        });
        store.dispatch('Appointments/triggerSocketEvent', {
          response,
          appointment: props.appointment
        });
        novaCore.removeArrayItem(fieldsInEditMode.value, fieldName);
      } catch (err) {
        notify(
          err?.response?.data?.message ??
            t(
              'appointment.dialogs.appointmentDetailsDialog.messages.anErrorOccurredWhileUpdatingTheAppointment'
            ),
          'error',
          {
            title: notificationsHelper.getNotificationTitleByRespCode(
              err?.response?.data?.errorCode
            )
          }
        );
      } finally {
        novaCore.removeArrayItem(fieldsInLoadingState.value, fieldName);
      }
    }

    const { etaCondition, getEtaConditionMeta } = useEta();

    function updateEtaCondition(condition) {
      etaCondition.value = condition;
    }

    const computedEta = computed(() =>
      novaCore.formatDateTimeWithMilitarySupport(
        props.appointment.eta,
        warehouse.value.timezone,
        novaCore.LuxonDateTimeFormats.ShortMonthDayTimeAMPM,
        isMilitaryTimeEnabled.value,
        novaCore.LuxonDateTimeFormats.ShortMonthDayTime24
      )
    );

    const toggleFavoriteCarrier = async carrier => {
      if (org.value) {
        const favoritesList = org.value.favoriteCarrierIds || [];
        const index = favoritesList.findIndex(fav => fav === carrier.id);
        const isFavorite = index === -1;

        if (isFavorite) {
          favoritesList.push(carrier.id);
        } else {
          favoritesList.splice(index, 1);
        }
        org.value.favoriteCarrierIds = favoritesList;

        await axios
          .patch(`/org/${org.value.id}/favorite-carriers`, { favoriteCarrierIds: favoritesList })
          .then(async () => {
            notify(
              `Carrier ${
                isFavorite
                  ? t('appointment.dialogs.appointmentDetailsDialog.messages.addedToFavorites')
                  : t('appointment.dialogs.appointmentDetailsDialog.messages.removedFromFavorites')
              }`
            );

            const mixpanelData = {
              'Entry Point': 'Appointment Details Modal',
              'Carrier Contact Name': novaCore.namesToFullName(carrier.firstName, carrier.lastName),
              'Carrier Contact Email': carrier.email,
              'Carrier Company Name': carrier.company?.name,
              'Carrier Company ID': carrier.company?.id,
              'Org ID': org.value.id,
              'Org Name': org.value.name
            };

            mixpanel.track(
              MixpanelEvents.MODULE.CARRIER.FAVORITES[
                `${isFavorite ? t('global.basicTerms.added') : t('global.basicTerms.removed')}`
              ],
              mixpanelData
            );
          });
      }
    };

    const handleFormFieldUpdate = formField => {
      notify(
        t('appointment.dialogs.appointmentDetailsDialog.messages.formFieldUpdatedSuccessfully'),
        'success'
      );
      const mixpanelData = {
        entry_point: 'Appointment Details Modal',
        field: formField.label,
        appointment_id: props.appointment.id,
        warehouse_id: warehouse.value.id,
        warehouse_name: warehouse.value.name,
        org_id: org.value.id,
        org_name: org.value.name
      };

      mixpanel.track('Updated: Form Field', mixpanelData);

      // This is the internal event that will be used to update the form field
      eventHub.$emit('customformdata-updated', formField);
    };

    watch(ccEmails, async (newVal, oldVal) => {
      if (isEqual(newVal, oldVal) || isEqual(props.appointment.ccEmails, newVal)) return;
      await updateCCEmails(oldVal);
    });

    watch(
      () => props.appointment,
      () => {
        etaCondition.value = novaCore.getEtaCondition(
          props.appointment.start,
          props.appointment.eta,
          warehouse.value.timezone
        );

        loadTypeId.value = props.appointment.loadTypeId;
        ccEmails.value = props.appointment.ccEmails;
        selectedCarrier.value = props.appointment.userId;
        notes.value = props.appointment.notes;
        refNumber.value = props.appointment.refNumber;
      },
      { immediate: true }
    );

    return {
      EditableDetailsFields,
      handleEditClick,
      handleFormFieldUpdate,
      ccEmails,
      fieldsInEditMode,
      loadTypeId,
      uniq,
      saveField,
      fieldsInLoadingState,
      refNumber,
      handleNevermindClick,
      appointmentDataRefs,
      novaCore,
      computedEta,
      etaCondition,
      updateEtaCondition,
      isMilitaryTimeEnabled,
      org,
      getEtaConditionMeta,
      selectedCarrier,
      toggleFavoriteCarrier,
      cleanUpCustomFormsData,
      capitalize,
      warehouse,
      toKebabCase,
      customFormsComponentsMapping,
      isFeatureEnabledForWarehouse,
      t
    };
  },
  computed: {
    refNumDisplayName() {
      const displayName = this.$refNumSettings(this.appointment.warehouse).displayName;
      return getTranslatedRefNumberDisplayName(displayName);
    },
    shouldShowRefNumber() {
      return (
        this.$refNumSettings(this.appointment.warehouse).isVisible !== false ||
        this.appointment.refNumber?.length > 0
      );
    }
  }
};
</script>

<style scoped lang="scss">
.appointment-details {
  background-color: $color-background-primary;
}

.sidebar-content {
  display: flex;
  gap: 16px;
  background-color: $color-background-primary;
}

.sidebar {
  width: 317px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.html-content) p:last-child {
  margin-bottom: 0;
}

.data-group {
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  gap: 4px;

  &.action-group:not(.is-in-edit-mode) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    i {
      font-size: 20px !important;
    }
  }

  &:not(:first-child):not(.no-divider) {
    border-top: 1px solid $color-line-divider;
    padding-top: 12px;
  }

  .data-header {
    font-size: 14px;
    font-weight: 600;
    color: $color-text-tertiary;
  }
}

:deep(.copy-btn-wrapper) i {
  font-size: 20px !important;
}

:deep(.v-autocomplete.v-select.v-input--is-focused) input {
  min-width: 30px !important;
}
</style>
